import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import ManageSearchOutlinedIcon from "@mui/icons-material/ManageSearchOutlined";
import SearchOffOutlinedIcon from "@mui/icons-material/SearchOffOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { styled, Tooltip, TooltipProps, tooltipClasses, Typography } from "@mui/material";
import { Button } from "@fluentui/react-components";
import React, { memo, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import i18next from "../../i18n/i18n";
import { appInfo, allProductAutoSelectStaticDefaults } from "../../authConfig";
import {
    selectSuggestionsStage,
    selectSuggestionsMatchedProducts,
    selectSuggestionsMessage,
    selectSuggestionsIsLocked,
    selectSuggestionsAllProducts,
    selectSuggestionsLoadingPhase,
    selectSuggestionsSearchCitations,
    selectSuggestionsScoreCandidates,
    selectSuggestionsSkipReasonCode
} from "../../Store/selectors";
import { AllProductSuggestionsAction } from "../../Store/AllProductSuggestionsReducer";
import type { AllProductAutoSelectRuntimeConfig, AllProductMatchedProduct } from "../QuestionInput";
import type {
    AllProductCitationInsight,
    AllProductScoreInsight,
    BrowsableProduct,
    BrowsableVersion,
    SuggestionsLoadingPhase
} from "../../Store/AllProductSuggestionsReducer";
import styles from "./AllProductSuggestionsPanel.module.css";

// ── Light tooltip ─────────────────────────────────────────────────
const LightTooltip = styled(({ className, ...props }: TooltipProps) => <Tooltip {...props} classes={{ popper: className }} />)(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: theme.palette.common.white,
        color: "rgba(0, 0, 0, 0.87)",
        boxShadow: theme.shadows[1],
        fontSize: 11
    }
}));

// ── Grouping helpers ─────────────────────────────────────────────
type VersionGroup = { label: string; items: AllProductMatchedProduct[] };
type ProductGroup = {
    productText: string;
    bareItems: AllProductMatchedProduct[];
    versionGroups: Map<string, VersionGroup>;
};

function groupProducts(matchedProducts: AllProductMatchedProduct[]): ProductGroup[] {
    const productMap = new Map<string, ProductGroup>();
    for (const item of matchedProducts) {
        const pText = item.productText;
        if (!productMap.has(pText)) {
            productMap.set(pText, { productText: pText, bareItems: [], versionGroups: new Map() });
        }
        const group = productMap.get(pText)!;
        if (item.productVersion) {
            const segments = (item.displayText ?? "").split(" > ");
            const versionLabel = segments.length >= 2 ? segments[1] : "";
            const vKey = [item.productVersion.manual_version_key, item.productVersion.faq_version_key].join("|");
            if (!group.versionGroups.has(vKey)) {
                group.versionGroups.set(vKey, { label: versionLabel, items: [] });
            }
            group.versionGroups.get(vKey)!.items.push(item);
        } else {
            group.bareItems.push(item);
        }
    }
    const groups = [...productMap.values()];
    groups.sort((a, b) => {
        const confidenceA = Math.max(
            ...[...a.bareItems, ...[...a.versionGroups.values()].flatMap(vg => vg.items)].map(item => {
                const confidenceValue = Number(item.confidence);
                return Number.isFinite(confidenceValue) ? confidenceValue : -1;
            }),
            -1
        );
        const confidenceB = Math.max(
            ...[...b.bareItems, ...[...b.versionGroups.values()].flatMap(vg => vg.items)].map(item => {
                const confidenceValue = Number(item.confidence);
                return Number.isFinite(confidenceValue) ? confidenceValue : -1;
            }),
            -1
        );
        if (confidenceB !== confidenceA) {
            return confidenceB - confidenceA;
        }
        const totalA = [...a.bareItems, ...[...a.versionGroups.values()].flatMap(vg => vg.items)].reduce((sum, item) => sum + item.count, 0);
        const totalB = [...b.bareItems, ...[...b.versionGroups.values()].flatMap(vg => vg.items)].reduce((sum, item) => sum + item.count, 0);
        return totalB - totalA;
    });
    return groups;
}

// ── Component ────────────────────────────────────────────────────
interface AllProductSuggestionsPanelProps {
    className?: string;
    allProducts?: BrowsableProduct[];
    onSelectProduct?: (product: AllProductMatchedProduct) => void | Promise<void>;
    onContinueWithAll?: () => void | Promise<void>;
    onCancel?: () => void | Promise<void>;
    allProductAutoSelectConfig?: AllProductAutoSelectRuntimeConfig;
    isAdminUser?: boolean;
    allowContinueWithAll?: boolean;
}

type ActiveTab = "suggestions" | "browse";
type PhaseViewKey = "search" | "score" | "apply" | "skip";
type PhaseContextRow = { label: string; value: string };

const AllProductSuggestionsPanelComponent: React.FC<AllProductSuggestionsPanelProps> = ({
    className,
    allProducts = [],
    onSelectProduct,
    onContinueWithAll,
    onCancel,
    allProductAutoSelectConfig,
    isAdminUser = false,
    allowContinueWithAll
}) => {
    // Backward-compatible fallback: if parent does not pass the computed value,
    // retain legacy global-only behavior.
    const enableAllProductForAllUsers = appInfo?.enable_all_product_for_all_users === true;
    const canContinueWithAll = allowContinueWithAll ?? enableAllProductForAllUsers;
    const dispatch = useDispatch();

    const stage = useSelector(selectSuggestionsStage);
    const matchedProducts = useSelector(selectSuggestionsMatchedProducts);
    const message = useSelector(selectSuggestionsMessage);
    const isLocked = useSelector(selectSuggestionsIsLocked);
    const loadingPhase = useSelector(selectSuggestionsLoadingPhase);
    const searchCitations = useSelector(selectSuggestionsSearchCitations);
    const scoreCandidates = useSelector(selectSuggestionsScoreCandidates);
    const skipReasonCode = useSelector(selectSuggestionsSkipReasonCode);
    const reduxAllProducts = useSelector(selectSuggestionsAllProducts);

    const [activeTab, setActiveTab] = useState<ActiveTab>("suggestions");
    const [browseSearch, setBrowseSearch] = useState("");
    const [expandedCatalogProductKey, setExpandedCatalogProductKey] = useState<string | null>(null);
    const [catalogVersionExpansionOverrides, setCatalogVersionExpansionOverrides] = useState<Record<string, boolean>>({});
    const [collapsedSuggestionGroups, setCollapsedSuggestionGroups] = useState<Set<string>>(new Set());
    const [selectedPhaseView, setSelectedPhaseView] = useState<PhaseViewKey>("search");
    const [isPhaseViewPinned, setIsPhaseViewPinned] = useState<boolean>(false);
    const [isOverviewCollapsed, setIsOverviewCollapsed] = useState<boolean>(false);
    const browseListRef = React.useRef<HTMLDivElement | null>(null);
    const canViewDiagnostics = !!isAdminUser;
    const showPhaseTimeline = !!isAdminUser;

    const isLoading = stage === "loading";
    const isSelection = stage === "select";
    const isConfirm = stage === "confirm";
    const isZeroSuggestions = isConfirm && matchedProducts.length === 0;
    const isBrowseSearchActive = browseSearch.trim().length > 0;

    const groupedProducts = useMemo(() => groupProducts(matchedProducts), [matchedProducts]);

    // Show ALL-confirm path only when effective permission allows it.
    const confirmMessage = canContinueWithAll
        ? i18next.t("questionInput.allProductValidation.confirmMessage")
        : i18next.t("questionInput.allProductValidation.matchedMessageNoAll");
    const loadingMessage = i18next.t("questionInput.allProductValidation.loadingMessage");
    const continueLabel = i18next.t("questionInput.allProductValidation.continueWithAll");
    const cancelLabel = i18next.t("questionInput.allProductValidation.cancel");
    const suggestionsTabLabel = i18next.t("questionInput.allProductValidation.suggestionsTabLabel");
    const browseTabLabel = i18next.t("questionInput.allProductValidation.browseTabLabel");
    const browseHintText = i18next.t("questionInput.allProductValidation.browseHint");
    const browseSearchPlaceholder = i18next.t("questionInput.allProductValidation.browseSearchPlaceholder");
    const browseNoResultsText = i18next.t("questionInput.allProductValidation.browseNoResults");
    const browseCatalogEmptyText = i18next.t("questionInput.allProductValidation.browseCatalogEmpty");
    const browseCatalogHelpText = i18next.t("questionInput.allProductValidation.browseCatalogHelp");
    const browseVersionsSectionLabel = i18next.t("questionInput.allProductValidation.browseVersionsSectionLabel");
    const browseSubProductsSectionLabel = i18next.t("questionInput.allProductValidation.browseSubProductsSectionLabel");
    const browseSelectLabel = i18next.t("questionInput.allProductValidation.browseSelectLabel");
    const closePanelLabel = i18next.t("questionInput.allProductValidation.closePanelLabel");
    const overviewShowLabel = i18next.t("questionInput.allProductValidation.overviewShowLabel");
    const overviewHideLabel = i18next.t("questionInput.allProductValidation.overviewHideLabel");
    const groupExpandLabel = i18next.t("questionInput.allProductValidation.groupExpandLabel");
    const groupCollapseLabel = i18next.t("questionInput.allProductValidation.groupCollapseLabel");
    const zeroSuggestionsTitle = i18next.t("questionInput.allProductValidation.zeroSuggestionsTitle");
    const zeroSuggestionsBody = i18next.t("questionInput.allProductValidation.zeroSuggestionsBody");
    const zeroSuggestionsBrowseCta = i18next.t("questionInput.allProductValidation.zeroSuggestionsBrowseCta");
    const zeroSuggestionsBrowseLead = i18next.t("questionInput.allProductValidation.zeroSuggestionsBrowseLead");
    const countBadgeLabel = i18next.t("questionInput.allProductValidation.matchCountLabel");
    const confidenceBadgeLabel = i18next.t("questionInput.allProductValidation.confidenceLabel");
    const scoreLegendText = i18next.t("questionInput.allProductValidation.scoreLegendText");
    const autoSelectingProgressLabel = i18next.t("questionInput.allProductValidation.autoSelectingProgressLabel");
    const loadingPhaseSearchCitationsLabel = i18next.t("questionInput.allProductValidation.loadingPhaseSearchCitations");
    const loadingPhaseScoreCandidatesLabel = i18next.t("questionInput.allProductValidation.loadingPhaseScoreCandidates");
    const loadingPhaseApplyBestMatchLabel = i18next.t("questionInput.allProductValidation.loadingPhaseApplyBestMatch");
    const loadingPhaseSkipAutoSelectionLabel = i18next.t("questionInput.allProductValidation.loadingPhaseSkipAutoSelection");
    const compactPhaseSearchHint = i18next.t("questionInput.allProductValidation.compactPhaseSearchHint", {
        defaultValue: loadingMessage
    });
    const compactPhaseScoreHint = i18next.t("questionInput.allProductValidation.compactPhaseScoreHint", {
        defaultValue: loadingPhaseScoreCandidatesLabel
    });
    const autoSelectionDecisionTopLabel = i18next.t("questionInput.allProductValidation.autoSelectionDecisionTopLabel");
    const autoSelectionDecisionNextLabel = i18next.t("questionInput.allProductValidation.autoSelectionDecisionNextLabel");
    const autoSelectionDecisionGapLabel = i18next.t("questionInput.allProductValidation.autoSelectionDecisionGapLabel");
    const autoSelectionDecisionRequiredMarginLabel = i18next.t("questionInput.allProductValidation.autoSelectionDecisionRequiredMarginLabel");
    const phaseMetaCandidateLabel = i18next.t("questionInput.allProductValidation.phaseMetaCandidateLabel");
    const phaseMetaRunnerUpLabel = i18next.t("questionInput.allProductValidation.phaseMetaRunnerUpLabel");
    const phaseMetaCandidatesLabel = i18next.t("questionInput.allProductValidation.phaseMetaCandidatesLabel");
    const phaseMetaCitationsLabel = i18next.t("questionInput.allProductValidation.phaseMetaCitationsLabel");
    const phaseMetaUniqueCitationsLabel = i18next.t("questionInput.allProductValidation.phaseMetaUniqueCitationsLabel");
    const searchDetailsTitle = i18next.t("questionInput.allProductValidation.searchDetailsTitle");
    const searchDetailsEmpty = i18next.t("questionInput.allProductValidation.searchDetailsEmpty");
    const scoreDetailsTitle = i18next.t("questionInput.allProductValidation.scoreDetailsTitle");
    const scoreDetailsEmpty = i18next.t("questionInput.allProductValidation.scoreDetailsEmpty");
    const detailsSourceLabel = i18next.t("questionInput.allProductValidation.detailsSourceLabel");
    const detailsRerankerScoreLabel = i18next.t("questionInput.allProductValidation.detailsRerankerScoreLabel");
    const scoreDetailsHitsLabel = i18next.t("questionInput.allProductValidation.scoreDetailsHitsLabel");
    const scoreDetailsAverageLabel = i18next.t("questionInput.allProductValidation.scoreDetailsAverageLabel");
    const scoreDetailsPeakLabel = i18next.t("questionInput.allProductValidation.scoreDetailsPeakLabel");
    const scoreDetailsSamplesLabel = i18next.t("questionInput.allProductValidation.scoreDetailsSamplesLabel");
    const manualSelectionSkipReasonLabel = i18next.t("questionInput.allProductValidation.manualSelectionSkipReasonLabel");
    const configuredMinMargin = Number(allProductAutoSelectConfig?.minMargin ?? (appInfo as any)?.all_product_auto_select?.min_margin);
    const defaultMinMargin = Number(allProductAutoSelectStaticDefaults?.min_margin);
    const minMarginThreshold = Number.isFinite(configuredMinMargin) ? configuredMinMargin : Number.isFinite(defaultMinMargin) ? defaultMinMargin : 10;
    const configuredMinConfidence = Number(allProductAutoSelectConfig?.minConfidence ?? (appInfo as any)?.all_product_auto_select?.min_confidence);
    const defaultMinConfidence = Number(allProductAutoSelectStaticDefaults?.min_confidence);
    const minConfidenceThreshold = Number.isFinite(configuredMinConfidence)
        ? configuredMinConfidence
        : Number.isFinite(defaultMinConfidence)
          ? defaultMinConfidence
          : 90;
    const configuredMinHits = Number(allProductAutoSelectConfig?.minHits ?? (appInfo as any)?.all_product_auto_select?.min_hits);
    const defaultMinHits = Number(allProductAutoSelectStaticDefaults?.min_hits);
    const minHitsThreshold = Number.isFinite(configuredMinHits) ? configuredMinHits : Number.isFinite(defaultMinHits) ? defaultMinHits : 2;
    const configuredMinAvgRerankerScore = Number(
        allProductAutoSelectConfig?.minAvgRerankerScore ?? (appInfo as any)?.all_product_auto_select?.min_avg_reranker_score
    );
    const defaultMinAvgRerankerScore = Number(allProductAutoSelectStaticDefaults?.min_avg_reranker_score);
    const minAvgRerankerScoreThreshold = Number.isFinite(configuredMinAvgRerankerScore)
        ? configuredMinAvgRerankerScore
        : Number.isFinite(defaultMinAvgRerankerScore)
          ? defaultMinAvgRerankerScore
          : 2.5;
    const configuredMinAvgRerankerGap = Number(
        allProductAutoSelectConfig?.minAvgRerankerGap ?? (appInfo as any)?.all_product_auto_select?.min_avg_reranker_gap
    );
    const defaultMinAvgRerankerGap = Number(allProductAutoSelectStaticDefaults?.min_avg_reranker_gap);
    const minAvgRerankerGapThreshold = Number.isFinite(configuredMinAvgRerankerGap)
        ? configuredMinAvgRerankerGap
        : Number.isFinite(defaultMinAvgRerankerGap)
          ? defaultMinAvgRerankerGap
          : 0.2;
    const normalizedLoadingMessage = (message ?? "").trim();
    const resolvedLoadingPhase: SuggestionsLoadingPhase =
        loadingPhase ??
        (isLoading
            ? normalizedLoadingMessage === loadingPhaseScoreCandidatesLabel
                ? "score"
                : normalizedLoadingMessage.length === 0 ||
                    normalizedLoadingMessage === loadingMessage ||
                    normalizedLoadingMessage === loadingPhaseSearchCitationsLabel
                  ? "search"
                  : "apply"
            : null);
    const isAutoSelectingPhase = isLoading && resolvedLoadingPhase === "apply";
    const phaseMessages = new Set([
        loadingMessage.trim(),
        loadingPhaseSearchCitationsLabel.trim(),
        loadingPhaseScoreCandidatesLabel.trim(),
        loadingPhaseApplyBestMatchLabel.trim(),
        loadingPhaseSkipAutoSelectionLabel.trim()
    ]);
    const showLoadingStatusMessage = normalizedLoadingMessage.length > 0 && !phaseMessages.has(normalizedLoadingMessage);
    const confidenceOrderedCandidates = useMemo(
        () =>
            matchedProducts
                .map(candidate => {
                    const confidenceValue = Number(candidate.confidence);
                    return {
                        candidate,
                        confidence: Number.isFinite(confidenceValue) ? confidenceValue : null
                    };
                })
                .filter((entry): entry is { candidate: AllProductMatchedProduct; confidence: number } => entry.confidence !== null)
                .sort((left, right) => {
                    if (right.confidence !== left.confidence) return right.confidence - left.confidence;
                    if (right.candidate.count !== left.candidate.count) return right.candidate.count - left.candidate.count;
                    const leftLabel = String(left.candidate.displayText ?? left.candidate.productText ?? "");
                    const rightLabel = String(right.candidate.displayText ?? right.candidate.productText ?? "");
                    return leftLabel.localeCompare(rightLabel);
                }),
        [matchedProducts]
    );
    const topConfidence = confidenceOrderedCandidates[0]?.confidence;
    const nextConfidence = confidenceOrderedCandidates[1]?.confidence;
    const topConfidencePercent = Number.isFinite(topConfidence) ? Math.max(0, Math.min(100, Math.round(Number(topConfidence)))) : null;
    const nextConfidencePercent = Number.isFinite(nextConfidence) ? Math.max(0, Math.min(100, Math.round(Number(nextConfidence)))) : null;
    const confidenceGap =
        Number.isFinite(topConfidence) && Number.isFinite(nextConfidence) ? Math.max(0, Number(topConfidence) - Number(nextConfidence)) : null;
    const topCandidateMatchCount = confidenceOrderedCandidates[0]?.candidate.count ?? 0;
    const nextCandidateMatchCount = confidenceOrderedCandidates[1]?.candidate.count ?? 0;
    const totalCitationHits = matchedProducts.reduce((sum, product) => sum + Number(product.count || 0), 0);
    const citationSummaryCount = totalCitationHits > 0 ? totalCitationHits : searchCitations.length;
    const uniqueCitationCount = searchCitations.length;
    const totalCandidateCount = matchedProducts.length;
    const topCandidateText = confidenceOrderedCandidates[0]?.candidate.displayText ?? confidenceOrderedCandidates[0]?.candidate.productText ?? "-";
    const nextCandidateText = confidenceOrderedCandidates[1]?.candidate.displayText ?? confidenceOrderedCandidates[1]?.candidate.productText ?? "-";
    const rerankerOrderedCandidates = useMemo(
        () =>
            matchedProducts
                .map(candidate => {
                    const allRerankerScores = (Array.isArray(candidate.rerankerScores) ? candidate.rerankerScores : [])
                        .map(score => Number(score))
                        .filter(score => Number.isFinite(score))
                        .sort((left, right) => right - left);
                    const qualifiedRerankerScores = allRerankerScores.filter(score => score >= minAvgRerankerScoreThreshold);
                    const qualifiedCount = qualifiedRerankerScores.length;
                    const qualifiedAverage =
                        qualifiedCount > 0 ? Number((qualifiedRerankerScores.reduce((sum, score) => sum + score, 0) / qualifiedCount).toFixed(4)) : null;
                    return {
                        candidate,
                        totalScoreCount: allRerankerScores.length,
                        qualifiedCount,
                        qualifiedAverage
                    };
                })
                .sort((left, right) => {
                    const leftAverage = left.qualifiedAverage ?? Number.NEGATIVE_INFINITY;
                    const rightAverage = right.qualifiedAverage ?? Number.NEGATIVE_INFINITY;
                    if (rightAverage !== leftAverage) return rightAverage - leftAverage;
                    if (right.qualifiedCount !== left.qualifiedCount) return right.qualifiedCount - left.qualifiedCount;
                    if (right.totalScoreCount !== left.totalScoreCount) return right.totalScoreCount - left.totalScoreCount;
                    const leftLabel = String(left.candidate.displayText ?? left.candidate.productText ?? "");
                    const rightLabel = String(right.candidate.displayText ?? right.candidate.productText ?? "");
                    return leftLabel.localeCompare(rightLabel);
                }),
        [matchedProducts, minAvgRerankerScoreThreshold]
    );
    const rerankerEligibleCandidates = useMemo(
        () => rerankerOrderedCandidates.filter(entry => entry.qualifiedAverage !== null && entry.qualifiedCount >= minHitsThreshold),
        [minHitsThreshold, rerankerOrderedCandidates]
    );
    const topRerankerCandidate = rerankerEligibleCandidates[0] ?? null;
    const nextRerankerCandidate = rerankerEligibleCandidates[1] ?? null;
    const rerankerGap =
        topRerankerCandidate && nextRerankerCandidate && topRerankerCandidate.qualifiedAverage !== null && nextRerankerCandidate.qualifiedAverage !== null
            ? Number((topRerankerCandidate.qualifiedAverage - nextRerankerCandidate.qualifiedAverage).toFixed(4))
            : null;
    const isManualSelectionPhase = resolvedLoadingPhase === "skip" || isSelection;
    const finalPhaseViewKey: PhaseViewKey = isManualSelectionPhase ? "skip" : "apply";
    const phaseSequence: Array<{ key: PhaseViewKey; label: string }> = [
        { key: "search", label: loadingPhaseSearchCitationsLabel },
        { key: "score", label: loadingPhaseScoreCandidatesLabel },
        { key: finalPhaseViewKey, label: finalPhaseViewKey === "skip" ? loadingPhaseSkipAutoSelectionLabel : loadingPhaseApplyBestMatchLabel }
    ];
    const currentPhaseView: PhaseViewKey =
        resolvedLoadingPhase === "score"
            ? "score"
            : resolvedLoadingPhase === "apply"
              ? "apply"
              : resolvedLoadingPhase === "skip" || isSelection
                ? "skip"
                : "search";
    const currentPhaseIndex = phaseSequence.findIndex(phase => phase.key === currentPhaseView);
    const activePhaseView = isLoading || isSelection ? selectedPhaseView : currentPhaseView;
    const phaseLabelByView: Record<PhaseViewKey, string> = {
        search: loadingPhaseSearchCitationsLabel,
        score: loadingPhaseScoreCandidatesLabel,
        apply: loadingPhaseApplyBestMatchLabel,
        skip: loadingPhaseSkipAutoSelectionLabel
    };
    const activePhaseLabel = phaseSequence.find(phase => phase.key === activePhaseView)?.label ?? loadingPhaseSearchCitationsLabel;
    const activePhaseCardLabel = phaseLabelByView[activePhaseView];
    const activePhaseIsApply = activePhaseView === "apply";
    const activePhaseIsSkip = activePhaseView === "skip";
    const showPhaseTimelineExperience = showPhaseTimeline;
    const showCompactPhaseHint = !isAdminUser && isLoading && (currentPhaseView === "search" || currentPhaseView === "score");
    const compactPhaseHintText = currentPhaseView === "score" ? compactPhaseScoreHint : compactPhaseSearchHint;
    const isManualSelectionView = isSelection && activePhaseView === "skip";
    const showSelectionActions = !isSelection || isManualSelectionView;
    const showTagList = isManualSelectionView && matchedProducts.length > 0;
    const isNonAdminCompactDockMode = false;
    const renderedActiveTab: ActiveTab = activeTab;
    const manualConfidenceReasonText = useMemo(() => {
        if (!skipReasonCode) return null;
        if (skipReasonCode === "disabled") {
            return i18next.t("questionInput.allProductValidation.manualReasonDisabled");
        }
        if (skipReasonCode === "no_confidence") {
            return i18next.t("questionInput.allProductValidation.manualReasonNoConfidence");
        }
        if (skipReasonCode === "low_hits") {
            return i18next.t("questionInput.allProductValidation.manualReasonLowHits", {
                product: topCandidateText,
                hits: topCandidateMatchCount,
                requiredHits: minHitsThreshold
            });
        }
        if (skipReasonCode === "low_confidence") {
            return i18next.t("questionInput.allProductValidation.manualReasonLowConfidence", {
                product: topCandidateText,
                topConfidence: topConfidencePercent ?? 0,
                requiredConfidence: minConfidenceThreshold
            });
        }
        if (skipReasonCode === "low_margin") {
            return i18next.t("questionInput.allProductValidation.manualReasonLowMargin", {
                product: topCandidateText,
                margin: confidenceGap !== null ? Math.round(confidenceGap) : "-",
                requiredMargin: minMarginThreshold
            });
        }
        return null;
    }, [
        skipReasonCode,
        topCandidateText,
        topCandidateMatchCount,
        minHitsThreshold,
        topConfidencePercent,
        minConfidenceThreshold,
        minMarginThreshold,
        confidenceGap
    ]);
    const manualRerankerReasonText = useMemo(() => {
        if (skipReasonCode === "disabled") return null;
        if (matchedProducts.length === 0) return null;
        const topRerankerObserved = rerankerOrderedCandidates[0] ?? null;
        const topRerankerObservedLabel = topRerankerObserved?.candidate.displayText ?? topRerankerObserved?.candidate.productText ?? topCandidateText;
        if (!topRerankerObserved || topRerankerObserved.totalScoreCount === 0) {
            return i18next.t("questionInput.allProductValidation.manualReasonNoRerankerScores");
        }
        if (topRerankerObserved.qualifiedCount < minHitsThreshold) {
            return i18next.t("questionInput.allProductValidation.manualReasonLowRerankerHits", {
                product: topRerankerObservedLabel,
                hits: topRerankerObserved.qualifiedCount,
                requiredHits: minHitsThreshold,
                minScore: minAvgRerankerScoreThreshold.toFixed(2)
            });
        }
        if (topRerankerCandidate && topRerankerCandidate.qualifiedAverage !== null && topRerankerCandidate.qualifiedAverage < minAvgRerankerScoreThreshold) {
            return i18next.t("questionInput.allProductValidation.manualReasonLowRerankerScore", {
                product: topRerankerObservedLabel,
                topScore: topRerankerCandidate.qualifiedAverage.toFixed(2),
                requiredScore: minAvgRerankerScoreThreshold.toFixed(2)
            });
        }
        if (topRerankerCandidate && nextRerankerCandidate && rerankerGap !== null && rerankerGap < minAvgRerankerGapThreshold) {
            return i18next.t("questionInput.allProductValidation.manualReasonLowRerankerGap", {
                product: topRerankerObservedLabel,
                gap: rerankerGap.toFixed(2),
                requiredGap: minAvgRerankerGapThreshold.toFixed(2)
            });
        }
        return null;
    }, [
        matchedProducts.length,
        minAvgRerankerGapThreshold,
        minAvgRerankerScoreThreshold,
        minHitsThreshold,
        nextRerankerCandidate,
        rerankerGap,
        rerankerOrderedCandidates,
        skipReasonCode,
        topCandidateText,
        topRerankerCandidate
    ]);
    const manualSelectionReasonLines = useMemo(() => {
        const lines: string[] = [];
        if (manualRerankerReasonText) {
            lines.push(manualRerankerReasonText);
        }
        if (manualConfidenceReasonText && !lines.includes(manualConfidenceReasonText)) {
            lines.push(manualConfidenceReasonText);
        }
        return lines;
    }, [manualConfidenceReasonText, manualRerankerReasonText]);
    const phaseContextRows: PhaseContextRow[] =
        activePhaseView === "search"
            ? [
                  { label: phaseMetaCandidatesLabel, value: `${totalCandidateCount}` },
                  { label: phaseMetaCitationsLabel, value: `${citationSummaryCount}` },
                  { label: phaseMetaUniqueCitationsLabel, value: `${uniqueCitationCount}` },
                  { label: phaseMetaCandidateLabel, value: topCandidateText },
                  { label: confidenceBadgeLabel, value: topConfidencePercent === null ? "-" : `${topConfidencePercent}%` }
              ]
            : activePhaseView === "score"
              ? [
                    { label: phaseMetaCandidatesLabel, value: `${totalCandidateCount}` },
                    { label: phaseMetaCitationsLabel, value: `${citationSummaryCount}` },
                    { label: phaseMetaUniqueCitationsLabel, value: `${uniqueCitationCount}` },
                    { label: phaseMetaCandidateLabel, value: topCandidateText },
                    {
                        label: autoSelectionDecisionTopLabel,
                        value: `${topConfidencePercent === null ? "-" : `${topConfidencePercent}%`} / ${topCandidateMatchCount}`
                    },
                    { label: phaseMetaRunnerUpLabel, value: nextCandidateText },
                    {
                        label: autoSelectionDecisionNextLabel,
                        value: `${nextConfidencePercent === null ? "-" : `${nextConfidencePercent}%`} / ${nextCandidateMatchCount}`
                    },
                    { label: autoSelectionDecisionGapLabel, value: confidenceGap === null ? "-" : `${Math.round(confidenceGap)}%` },
                    { label: autoSelectionDecisionRequiredMarginLabel, value: `${Math.round(minMarginThreshold)}%` }
                ]
              : activePhaseView === "apply"
                ? [
                      { label: phaseMetaCandidatesLabel, value: `${totalCandidateCount}` },
                      { label: phaseMetaCandidateLabel, value: topCandidateText },
                      { label: confidenceBadgeLabel, value: topConfidencePercent === null ? "-" : `${topConfidencePercent}%` },
                      { label: countBadgeLabel, value: `${topCandidateMatchCount}` },
                      { label: phaseMetaCitationsLabel, value: `${citationSummaryCount}` },
                      { label: phaseMetaUniqueCitationsLabel, value: `${uniqueCitationCount}` }
                  ]
                : [
                      { label: phaseMetaCandidatesLabel, value: `${totalCandidateCount}` },
                      { label: phaseMetaCandidateLabel, value: topCandidateText },
                      { label: phaseMetaRunnerUpLabel, value: nextCandidateText },
                      {
                          label: autoSelectionDecisionTopLabel,
                          value: `${topConfidencePercent === null ? "-" : `${topConfidencePercent}%`} / ${topCandidateMatchCount}`
                      },
                      {
                          label: autoSelectionDecisionNextLabel,
                          value: `${nextConfidencePercent === null ? "-" : `${nextConfidencePercent}%`} / ${nextCandidateMatchCount}`
                      },
                      { label: autoSelectionDecisionGapLabel, value: confidenceGap === null ? "-" : `${Math.round(confidenceGap)}%` },
                      { label: autoSelectionDecisionRequiredMarginLabel, value: `${Math.round(minMarginThreshold)}%` },
                      { label: phaseMetaCitationsLabel, value: `${citationSummaryCount}` },
                      { label: phaseMetaUniqueCitationsLabel, value: `${uniqueCitationCount}` }
                  ];
    const fallbackScoreCandidates = useMemo<AllProductScoreInsight[]>(
        () =>
            matchedProducts
                .map((product, index) => ({
                    id: [
                        product.manual_key,
                        product.faq_key,
                        product.productVersion?.manual_version_key ?? "",
                        product.productVersion?.faq_version_key ?? "",
                        product.subProduct?.manual_sub_key ?? "",
                        product.subProduct?.faq_sub_key ?? "",
                        index
                    ].join("|"),
                    productLabel: String(product.displayText ?? product.productText ?? "-"),
                    confidence: Number.isFinite(Number(product.confidence)) ? Math.round(Number(product.confidence)) : null,
                    citationCount: Number.isFinite(Number(product.count)) ? Math.max(0, Number(product.count)) : 0,
                    averageScore: Number.isFinite(Number(product.averageScore)) ? Number(product.averageScore) : null,
                    maxScore: Number.isFinite(Number(product.maxScore)) ? Number(product.maxScore) : null,
                    scoreSampleCount: Number.isFinite(Number(product.scoreSampleCount)) ? Math.max(0, Number(product.scoreSampleCount)) : 0
                }))
                .sort((left, right) => {
                    const leftConfidence = Number(left.confidence ?? -1);
                    const rightConfidence = Number(right.confidence ?? -1);
                    if (rightConfidence !== leftConfidence) return rightConfidence - leftConfidence;
                    if (right.citationCount !== left.citationCount) return right.citationCount - left.citationCount;
                    return left.productLabel.localeCompare(right.productLabel);
                }),
        [matchedProducts]
    );
    const effectiveScoreCandidates: AllProductScoreInsight[] = scoreCandidates.length > 0 ? scoreCandidates : fallbackScoreCandidates;
    const searchPhaseCitations: AllProductCitationInsight[] = searchCitations.slice(0, 80);
    const scorePhaseCandidates: AllProductScoreInsight[] = effectiveScoreCandidates.slice(0, 40);
    const scoreComparisonCandidates = scorePhaseCandidates.filter(candidate => Number.isFinite(Number(candidate.confidence))).slice(0, 2);
    const formatDetailScore = (value: number | null): string => (value === null ? "-" : Number(value).toFixed(4));
    const showPrimaryStatusMessage = !isSelection;
    const panelStatusMessage = message && message.trim().length > 0 ? message : isConfirm || isSelection ? confirmMessage : "";
    const isLongStatusMessage = panelStatusMessage.length > 110;
    const formatConfidence = (confidence: number | undefined): string | null => {
        const numericConfidence = Number(confidence);
        if (!Number.isFinite(numericConfidence)) return null;
        return `${Math.round(Math.max(0, Math.min(100, numericConfidence)))}%`;
    };

    React.useEffect(() => {
        if (stage === "loading") {
            setActiveTab("suggestions");
            return;
        }
        if (isZeroSuggestions) {
            setActiveTab("browse");
        }
    }, [stage, isZeroSuggestions]);

    React.useEffect(() => {
        setExpandedCatalogProductKey(null);
        setCatalogVersionExpansionOverrides({});
    }, [browseSearch]);

    React.useEffect(() => {
        if (isLoading || isSelection) {
            if (!isPhaseViewPinned) {
                setSelectedPhaseView(currentPhaseView);
            }
            return;
        }
        setIsPhaseViewPinned(false);
    }, [isLoading, isSelection, currentPhaseView, isPhaseViewPinned]);

    // ── Action dispatchers ──────────────────────────────────────
    const dispatchSelectProduct = (product: AllProductMatchedProduct) => {
        if (onSelectProduct) {
            void onSelectProduct(product);
            return;
        }
        dispatch(AllProductSuggestionsAction.setPendingAction({ type: "selectProduct", product }));
    };

    const dispatchContinueWithAll = () => {
        if (onContinueWithAll) {
            void onContinueWithAll();
            return;
        }
        dispatch(AllProductSuggestionsAction.setPendingAction({ type: "continueWithAll" }));
    };

    const dispatchCancel = () => {
        if (onCancel) {
            void onCancel();
            return;
        }
        dispatch(AllProductSuggestionsAction.setPendingAction({ type: "cancel" }));
    };

    // ── Group-label click handlers ────────────────────────────── //
    const handleProductLabelClick = (group: ProductGroup) => {
        const firstItem = group.bareItems[0] ?? [...group.versionGroups.values()][0]?.items[0];
        if (!firstItem) return;
        const totalCount = [...group.bareItems, ...[...group.versionGroups.values()].flatMap(vg => vg.items)].reduce((sum, item) => sum + item.count, 0);
        dispatchSelectProduct({
            productText: firstItem.productText,
            manual_key: firstItem.manual_key,
            faq_key: firstItem.faq_key,
            count: totalCount,
            displayText: firstItem.productText
        });
    };

    const handleVersionLabelClick = (vGroup: VersionGroup) => {
        const firstItem = vGroup.items[0];
        if (!firstItem) return;
        const totalCount = vGroup.items.reduce((sum, item) => sum + item.count, 0);
        dispatchSelectProduct({
            productText: firstItem.productText,
            manual_key: firstItem.manual_key,
            faq_key: firstItem.faq_key,
            count: totalCount,
            displayText: firstItem.displayText?.split(" > ").slice(0, 2).join(" > ") ?? firstItem.productText,
            productVersion: firstItem.productVersion
        });
    };

    const toggleSuggestionGroupCollapse = (productText: string) => {
        setCollapsedSuggestionGroups(prev => {
            const next = new Set(prev);
            if (next.has(productText)) {
                next.delete(productText);
            } else {
                next.add(productText);
            }
            return next;
        });
    };

    // ── Browse tab helpers ────────────────────────────────────── //
    type FilteredSubItem = { manual_sub_key: string; faq_sub_key: string; text: string };
    type FilteredVersionItem = { version: BrowsableVersion; filteredSubs: FilteredSubItem[]; autoExpanded: boolean };
    type FilteredProductItem = {
        product: BrowsableProduct;
        filteredVersions: FilteredVersionItem[];
        filteredDirectSubs: FilteredSubItem[];
        autoExpanded: boolean;
    };

    const normalizeBrowseText = (value: unknown) => (typeof value === "string" ? value.trim() : "");

    const getProductSelectionKey = (p: Pick<BrowsableProduct, "manual_key" | "faq_key" | "text">) =>
        [p.manual_key ?? "", p.faq_key ?? "", normalizeBrowseText(p.text)].join("|");
    const getVersionSelectionKey = (v: Pick<BrowsableVersion, "manual_version_key" | "faq_version_key" | "text">) =>
        [v.manual_version_key ?? "", v.faq_version_key ?? "", normalizeBrowseText(v.text)].join("|");
    const getSubProductSelectionKey = (sub: Pick<FilteredSubItem, "manual_sub_key" | "faq_sub_key" | "text">) =>
        [sub.manual_sub_key ?? "", sub.faq_sub_key ?? "", normalizeBrowseText(sub.text)].join("|");
    const selectedLocale = sessionStorage?.getItem("selectedLocale") ?? "ja";
    const fallbackCatalog = useMemo(() => {
        const productCategories = appInfo?.product_categories?.[selectedLocale];
        return Array.isArray(productCategories) ? productCategories : [];
    }, [selectedLocale]);

    // Filter out the ALL pseudo-entry.
    // Priority: prop (from Chat.tsx loadProductFilter) → Redux (set by same loadProductFilter)
    const browsableProducts = useMemo((): BrowsableProduct[] => {
        const raw: any[] = allProducts.length > 0 ? allProducts : reduxAllProducts.length > 0 ? reduxAllProducts : fallbackCatalog;
        return raw.filter(
            (p: any) => p && normalizeBrowseText(p.text) && normalizeBrowseText(p.text) !== "ALL" && !(p.manual_key === "0" && p.faq_key === "0")
        );
    }, [allProducts, reduxAllProducts, fallbackCatalog]);

    const sanitizeSubProducts = (entries: any[]): FilteredSubItem[] =>
        entries
            .map((entry: any) => ({
                manual_sub_key: entry?.manual_sub_key ?? "",
                faq_sub_key: entry?.faq_sub_key ?? "",
                text: normalizeBrowseText(entry?.text)
            }))
            .filter(entry => entry.text);

    const splitBrowseChildren = (product: BrowsableProduct): { versions: BrowsableVersion[]; directSubs: FilteredSubItem[] } => {
        const versions: BrowsableVersion[] = [];
        const directSubs: FilteredSubItem[] = [];

        for (const rawEntry of product.versions ?? []) {
            const entry: any = rawEntry;
            const text = normalizeBrowseText(entry?.text);
            const hasVersionKeys = Boolean(entry?.manual_version_key || entry?.faq_version_key);
            const hasDirectSubKeys = Boolean(entry?.manual_sub_key || entry?.faq_sub_key);
            const subVersions = sanitizeSubProducts(Array.isArray(entry?.sub_versions) ? entry.sub_versions : []);

            if (hasDirectSubKeys) {
                if (text) {
                    directSubs.push({
                        manual_sub_key: entry.manual_sub_key ?? "",
                        faq_sub_key: entry.faq_sub_key ?? "",
                        text
                    });
                }
                continue;
            }

            if (!hasVersionKeys || !text) {
                directSubs.push(...subVersions);
                continue;
            }

            versions.push({
                ...entry,
                text,
                sub_versions: subVersions
            });
        }

        return { versions, directSubs };
    };

    const browseCatalogCards = useMemo(
        () =>
            browsableProducts.map((product, index) => {
                const { versions, directSubs } = splitBrowseChildren(product);
                const versionItems = versions.map(version => ({
                    version,
                    filteredSubs: version.sub_versions ?? [],
                    autoExpanded: false
                }));
                const nestedSubCount = versionItems.reduce((sum, versionItem) => sum + versionItem.filteredSubs.length, 0);
                return {
                    key: `catalog|${getProductSelectionKey(product)}|${index}`,
                    product,
                    versions: versionItems,
                    directSubs,
                    versionCount: versionItems.length,
                    subProductCount: directSubs.length + nestedSubCount
                };
            }),
        [browsableProducts]
    );

    const filteredBrowseResults = useMemo((): FilteredProductItem[] => {
        const q = browseSearch.trim().toLowerCase();
        if (!q) {
            return browsableProducts.map(p => {
                const { versions, directSubs } = splitBrowseChildren(p);
                return {
                    product: p,
                    filteredVersions: versions.map(v => ({ version: v, filteredSubs: v.sub_versions ?? [], autoExpanded: true })),
                    filteredDirectSubs: directSubs,
                    autoExpanded: true
                };
            });
        }
        const results: FilteredProductItem[] = [];
        for (const p of browsableProducts) {
            const { versions, directSubs } = splitBrowseChildren(p);
            const pMatches = normalizeBrowseText(p.text).toLowerCase().includes(q);
            const matchingDirectSubs = directSubs.filter(sub => sub.text.toLowerCase().includes(q));
            const filteredVersions: FilteredVersionItem[] = [];
            for (const v of versions) {
                const vMatches = v.text?.toLowerCase().includes(q) ?? false;
                const matchingSubs = (v.sub_versions ?? []).filter((s: any) => s.text?.toLowerCase().includes(q));
                const subMatches = matchingSubs.length > 0;
                if (pMatches || vMatches || subMatches) {
                    filteredVersions.push({
                        version: v,
                        filteredSubs: pMatches || vMatches ? (v.sub_versions ?? []) : matchingSubs,
                        autoExpanded: true
                    });
                }
            }
            if (pMatches || filteredVersions.length > 0 || matchingDirectSubs.length > 0) {
                results.push({
                    product: p,
                    filteredVersions: pMatches ? versions.map(v => ({ version: v, filteredSubs: v.sub_versions ?? [], autoExpanded: true })) : filteredVersions,
                    filteredDirectSubs: pMatches ? directSubs : matchingDirectSubs,
                    autoExpanded: true
                });
            }
        }
        return results;
    }, [browsableProducts, browseSearch]);

    const searchBrowseCards = useMemo(
        () =>
            filteredBrowseResults.map((item, index) => ({
                key: `search|${getProductSelectionKey(item.product)}|${index}`,
                product: item.product,
                versions: item.filteredVersions,
                directSubs: item.filteredDirectSubs,
                versionCount: item.filteredVersions.length,
                subProductCount: item.filteredDirectSubs.length + item.filteredVersions.reduce((sum, version) => sum + version.filteredSubs.length, 0)
            })),
        [filteredBrowseResults]
    );

    const visibleBrowseCards = isBrowseSearchActive ? searchBrowseCards : browseCatalogCards;

    const getBrowseDomKey = (key: string) => encodeURIComponent(key);

    const scrollBrowseNodeIntoView = (selector: string) => {
        let frameOne = 0;
        let frameTwo = 0;
        const scrollTarget = () => {
            const node = browseListRef.current?.querySelector<HTMLElement>(selector);
            if (!node) {
                return;
            }
            node.scrollIntoView({
                behavior: "smooth",
                block: "start",
                inline: "nearest"
            });
        };
        frameOne = window.requestAnimationFrame(() => {
            scrollTarget();
            frameTwo = window.requestAnimationFrame(scrollTarget);
        });
        return () => {
            window.cancelAnimationFrame(frameOne);
            window.cancelAnimationFrame(frameTwo);
        };
    };

    const scrollBrowseCardIntoView = (cardKey: string) => scrollBrowseNodeIntoView(`[data-browse-card-key="${getBrowseDomKey(cardKey)}"]`);

    const scrollBrowseVersionIntoView = (versionKey: string) => scrollBrowseNodeIntoView(`[data-browse-version-key="${getBrowseDomKey(versionKey)}"]`);

    React.useLayoutEffect(() => {
        if (activeTab !== "browse") {
            return;
        }
        const resetScroll = () => {
            if (!browseListRef.current) {
                return;
            }
            browseListRef.current.scrollTop = 0;
            browseListRef.current.scrollTo({ top: 0, left: 0, behavior: "auto" });
        };
        let frameOne = 0;
        let frameTwo = 0;
        frameOne = window.requestAnimationFrame(() => {
            resetScroll();
            frameTwo = window.requestAnimationFrame(resetScroll);
        });
        return () => {
            window.cancelAnimationFrame(frameOne);
            window.cancelAnimationFrame(frameTwo);
        };
    }, [activeTab, browseSearch, visibleBrowseCards.length]);

    const dispatchClose = () => {
        dispatchCancel();
    };

    const handleCatalogToggleProduct = (productKey: string) => {
        setCatalogVersionExpansionOverrides({});
        setExpandedCatalogProductKey(previousKey => (previousKey === productKey ? null : productKey));
        scrollBrowseCardIntoView(productKey);
    };

    const handleCatalogOpenProduct = (productKey: string) => {
        if (expandedCatalogProductKey !== productKey) {
            setCatalogVersionExpansionOverrides({});
            setExpandedCatalogProductKey(productKey);
        }
        scrollBrowseCardIntoView(productKey);
    };

    const handleCatalogToggleVersion = (versionKey: string, isExpanded: boolean) => {
        setCatalogVersionExpansionOverrides(prev => ({
            ...prev,
            [versionKey]: !isExpanded
        }));
        scrollBrowseVersionIntoView(versionKey);
    };

    // Dispatch from browse: product only
    const handleBrowseConfirmProduct = (p: BrowsableProduct) => {
        dispatchSelectProduct({
            productText: p.text,
            manual_key: p.manual_key,
            faq_key: p.faq_key,
            count: 0,
            displayText: p.text,
            fromBrowse: true
        });
    };

    // Dispatch from browse: product + version
    const handleBrowseConfirmVersion = (p: BrowsableProduct, v: BrowsableVersion) => {
        dispatchSelectProduct({
            productText: p.text,
            manual_key: p.manual_key,
            faq_key: p.faq_key,
            count: 0,
            displayText: `${p.text} > ${v.text}`,
            fromBrowse: true,
            productVersion: { manual_version_key: v.manual_version_key, faq_version_key: v.faq_version_key }
        });
    };

    // Dispatch from browse: product + version + subproduct
    const handleBrowseConfirmSubProduct = (p: BrowsableProduct, v: BrowsableVersion, sub: { manual_sub_key: string; faq_sub_key: string; text: string }) => {
        dispatchSelectProduct({
            productText: p.text,
            manual_key: p.manual_key,
            faq_key: p.faq_key,
            count: 0,
            displayText: `${p.text} > ${v.text} > ${sub.text}`,
            fromBrowse: true,
            productVersion: { manual_version_key: v.manual_version_key, faq_version_key: v.faq_version_key },
            subProduct: { manual_sub_key: sub.manual_sub_key, faq_sub_key: sub.faq_sub_key }
        });
    };

    const handleBrowseConfirmDirectSubProduct = (p: BrowsableProduct, sub: { manual_sub_key: string; faq_sub_key: string; text: string }) => {
        dispatchSelectProduct({
            productText: p.text,
            manual_key: p.manual_key,
            faq_key: p.faq_key,
            count: 0,
            displayText: `${p.text} > ${sub.text}`,
            fromBrowse: true,
            subProduct: { manual_sub_key: sub.manual_sub_key, faq_sub_key: sub.faq_sub_key }
        });
    };

    const renderBrowseCatalogCard = ({
        key: cardKey,
        product,
        versions,
        directSubs,
        versionCount,
        subProductCount
    }: {
        key: string;
        product: BrowsableProduct;
        versions: FilteredVersionItem[];
        directSubs: FilteredSubItem[];
        versionCount: number;
        subProductCount: number;
    }) => {
        const isCardExpanded = expandedCatalogProductKey === cardKey;
        const isCardExpandable = versionCount > 0 || subProductCount > 0;
        const domCardKey = getBrowseDomKey(cardKey);

        return (
            <div
                key={cardKey}
                data-browse-card-key={domCardKey}
                className={`${styles.browseCatalogCard} ${isCardExpanded ? styles.browseCatalogCardExpanded : ""}`}
            >
                <div className={styles.browseCatalogCardHeader}>
                    <div className={styles.browseCatalogTitleBlock}>
                        {isCardExpandable ? (
                            <button
                                type="button"
                                className={styles.browseCatalogTitleButton}
                                onClick={() => handleCatalogToggleProduct(cardKey)}
                                disabled={isLocked}
                            >
                                <span className={styles.browseCatalogChevron}>{isCardExpanded ? "▾" : "▸"}</span>
                                <span className={styles.browseCatalogCardTitle}>{product.text}</span>
                            </button>
                        ) : (
                            <div className={styles.browseCatalogStaticTitle}>
                                <span className={styles.browseCatalogCardTitle}>{product.text}</span>
                            </div>
                        )}
                        <div className={styles.browseCatalogMeta}>
                            {versionCount > 0 && (
                                <button
                                    type="button"
                                    className={styles.browseCatalogMetaButton}
                                    onClick={() => handleCatalogOpenProduct(cardKey)}
                                    disabled={isLocked}
                                >
                                    <span className={styles.browseCatalogMetaBadge}>
                                        {i18next.t("questionInput.allProductValidation.browseVersionCountBadge", {
                                            count: versionCount
                                        })}
                                    </span>
                                </button>
                            )}
                            {subProductCount > 0 && (
                                <button
                                    type="button"
                                    className={styles.browseCatalogMetaButton}
                                    onClick={() => handleCatalogOpenProduct(cardKey)}
                                    disabled={isLocked}
                                >
                                    <span className={styles.browseCatalogMetaBadge}>
                                        {i18next.t("questionInput.allProductValidation.browseSubProductCountBadge", {
                                            count: subProductCount
                                        })}
                                    </span>
                                </button>
                            )}
                        </div>
                    </div>
                    <button
                        type="button"
                        className={styles.browseSelectBtn}
                        onClick={() => handleBrowseConfirmProduct(product)}
                        disabled={isLocked}
                        title={i18next.t("questionInput.allProductValidation.browseSelectTitle", { name: product.text })}
                    >
                        {browseSelectLabel}
                    </button>
                </div>
                {isCardExpanded && (
                    <div className={styles.browseCatalogDetail}>
                        {versions.length > 0 && (
                            <div className={styles.browseCatalogSection}>
                                <div className={styles.browseCatalogSectionHeader}>{browseVersionsSectionLabel}</div>
                                <div className={styles.browseVersionList}>
                                    {versions.map((versionItem, versionIndex) => {
                                        const version = versionItem.version;
                                        const filteredSubs = versionItem.filteredSubs;
                                        const versionKey = `${cardKey}|version|${getVersionSelectionKey(version)}|${versionIndex}`;
                                        const hasSubProducts = filteredSubs.length > 0;
                                        const isVersionExpanded = hasSubProducts ? (catalogVersionExpansionOverrides[versionKey] ?? false) : false;
                                        const domVersionKey = getBrowseDomKey(versionKey);
                                        return (
                                            <div key={versionKey} data-browse-version-key={domVersionKey} className={styles.browseVersionBlock}>
                                                <div
                                                    className={`${styles.browseVersionRow} ${
                                                        hasSubProducts && isVersionExpanded ? styles.browseVersionRowActive : ""
                                                    }`}
                                                >
                                                    {hasSubProducts ? (
                                                        <button
                                                            type="button"
                                                            className={styles.browseVersionName}
                                                            onClick={() => handleCatalogToggleVersion(versionKey, isVersionExpanded)}
                                                            disabled={isLocked}
                                                        >
                                                            <span className={styles.browseChevron}>{isVersionExpanded ? "▾" : "▸"}</span>
                                                            {version.text}
                                                        </button>
                                                    ) : (
                                                        <span className={styles.browseVersionLabel}>{version.text}</span>
                                                    )}
                                                    <button
                                                        type="button"
                                                        className={styles.browseSelectBtn}
                                                        onClick={() => handleBrowseConfirmVersion(product, version)}
                                                        disabled={isLocked}
                                                        title={i18next.t("questionInput.allProductValidation.browseSelectTitle", {
                                                            name: `${product.text} > ${version.text}`
                                                        })}
                                                    >
                                                        {browseSelectLabel}
                                                    </button>
                                                </div>
                                                {isVersionExpanded && hasSubProducts && (
                                                    <div className={styles.browseSubList}>
                                                        {filteredSubs.map((sub, subIndex) => (
                                                            <div
                                                                key={`${cardKey}|sub|${getSubProductSelectionKey(sub)}|${subIndex}`}
                                                                className={styles.browseSubRow}
                                                            >
                                                                <span className={styles.browseSubName}>{sub.text}</span>
                                                                <button
                                                                    type="button"
                                                                    className={styles.browseSelectBtn}
                                                                    onClick={() => handleBrowseConfirmSubProduct(product, version, sub)}
                                                                    disabled={isLocked}
                                                                    title={i18next.t("questionInput.allProductValidation.browseSelectTitle", {
                                                                        name: `${product.text} > ${version.text} > ${sub.text}`
                                                                    })}
                                                                >
                                                                    {browseSelectLabel}
                                                                </button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                        {directSubs.length > 0 && (
                            <div className={styles.browseCatalogSection}>
                                <div className={styles.browseCatalogSectionHeader}>{browseSubProductsSectionLabel}</div>
                                <div className={`${styles.browseSubList} ${styles.browseDirectSubList}`}>
                                    {directSubs.map((sub, subIndex) => (
                                        <div key={`${cardKey}|direct-sub|${getSubProductSelectionKey(sub)}|${subIndex}`} className={styles.browseSubRow}>
                                            <span className={styles.browseSubName}>{sub.text}</span>
                                            <button
                                                type="button"
                                                className={styles.browseSelectBtn}
                                                onClick={() => handleBrowseConfirmDirectSubProduct(product, sub)}
                                                disabled={isLocked}
                                                title={i18next.t("questionInput.allProductValidation.browseSelectTitle", {
                                                    name: `${product.text} > ${sub.text}`
                                                })}
                                            >
                                                {browseSelectLabel}
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    // ── Render ───────────────────────────────────────────────────
    return (
        <div className={`${styles.root} ${className ?? ""} ${isNonAdminCompactDockMode ? styles.rootCompactDocked : ""}`}>
            <div className={`${styles.panelViewport} ${isNonAdminCompactDockMode ? styles.panelViewportCompactDocked : ""}`}>
                <div className={`${styles.panel} ${isNonAdminCompactDockMode ? styles.panelCompactDocked : ""}`}>
                    {/* Header */}
                    <div className={styles.panelHeader}>
                        <div className={styles.panelTitle}>
                            <ManageSearchOutlinedIcon sx={{ fontSize: "15px", color: "#2f6feb", flexShrink: 0 }} />
                            <AutoAwesomeOutlinedIcon sx={{ fontSize: "13px", color: "#9c6fe4", flexShrink: 0, marginLeft: "-2px" }} />
                            {i18next.t("questionInput.allProductValidation.panelTitle")}
                        </div>
                    </div>

                    {/* Tabs */}
                    <div className={styles.tabBar}>
                        <button
                            type="button"
                            className={`${styles.tabBtn} ${activeTab === "suggestions" ? styles.tabBtnActive : ""}`}
                            onClick={() => setActiveTab("suggestions")}
                        >
                            <AutoAwesomeOutlinedIcon sx={{ fontSize: "12px" }} />
                            {suggestionsTabLabel}
                        </button>
                        <button
                            type="button"
                            className={`${styles.tabBtn} ${activeTab === "browse" ? styles.tabBtnActive : ""}`}
                            onClick={() => setActiveTab("browse")}
                        >
                            <CategoryOutlinedIcon sx={{ fontSize: "12px" }} />
                            {browseTabLabel}
                        </button>
                    </div>

                    {/* Body */}
                    <div className={styles.panelBody}>
                        {renderedActiveTab === "suggestions" ? (
                            <>
                                {isLoading ? (
                                    <>
                                        {showPhaseTimelineExperience && (
                                            <div className={styles.phaseTimeline} aria-live="polite">
                                                {phaseSequence.map((phase, phaseIndex) => {
                                                    const isFinalStep = phaseIndex === 2;
                                                    const stepStateClass =
                                                        phaseIndex < currentPhaseIndex
                                                            ? styles.phaseStepComplete
                                                            : phaseIndex === currentPhaseIndex
                                                              ? isFinalStep && isAutoSelectingPhase
                                                                  ? styles.phaseStepApplied
                                                                  : isFinalStep && currentPhaseView === "skip"
                                                                    ? styles.phaseStepSkip
                                                                    : styles.phaseStepActive
                                                              : styles.phaseStepUpcoming;
                                                    const isSelectedPhase = activePhaseView === phase.key;
                                                    return (
                                                        <button
                                                            key={`${phase.key}-${phaseIndex}`}
                                                            type="button"
                                                            className={`${styles.phaseStep} ${styles.phaseStepButton} ${stepStateClass} ${
                                                                isSelectedPhase ? styles.phaseStepSelected : ""
                                                            }`}
                                                            onClick={() => {
                                                                setSelectedPhaseView(phase.key);
                                                                setIsPhaseViewPinned(true);
                                                            }}
                                                            aria-pressed={isSelectedPhase}
                                                            disabled={isLocked}
                                                        >
                                                            <span className={styles.phaseStepBullet}>{phaseIndex + 1}</span>
                                                            <span className={styles.phaseStepLabel}>{phase.label}</span>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        )}

                                        <div
                                            className={`${styles.phaseCard} ${activePhaseIsApply ? styles.phaseCardApply : ""} ${
                                                activePhaseIsSkip ? styles.phaseCardSkip : ""
                                            }`}
                                        >
                                            <div
                                                className={`${styles.loadingPhaseRow} ${activePhaseIsApply ? styles.loadingPhaseRowApply : ""} ${
                                                    activePhaseIsSkip ? styles.loadingPhaseRowSkip : ""
                                                }`}
                                                aria-live="polite"
                                            >
                                                <span
                                                    className={`${styles.loadingPhasePulse} ${
                                                        activePhaseIsApply
                                                            ? styles.loadingPhasePulseApply
                                                            : activePhaseIsSkip
                                                              ? styles.loadingPhasePulseSkip
                                                              : ""
                                                    }`}
                                                />
                                                <span
                                                    className={`${styles.loadingPhaseText} ${
                                                        activePhaseIsApply ? styles.loadingPhaseTextApply : activePhaseIsSkip ? styles.loadingPhaseTextSkip : ""
                                                    }`}
                                                >
                                                    {activePhaseCardLabel}
                                                </span>
                                                <span
                                                    className={`${styles.loadingPhaseDots} ${
                                                        activePhaseIsApply ? styles.loadingPhaseDotsApply : activePhaseIsSkip ? styles.loadingPhaseDotsSkip : ""
                                                    }`}
                                                    aria-hidden="true"
                                                >
                                                    <span className={styles.loadingPhaseDot} />
                                                    <span className={styles.loadingPhaseDot} />
                                                    <span className={styles.loadingPhaseDot} />
                                                </span>
                                            </div>
                                            {isAutoSelectingPhase && activePhaseView === currentPhaseView && (
                                                <div className={styles.autoSelectingIndicator} aria-live="polite">
                                                    <AutoAwesomeOutlinedIcon className={styles.autoSelectingIcon} />
                                                    <span className={styles.autoSelectingText}>{autoSelectingProgressLabel}</span>
                                                    <span className={styles.autoSelectingDots}>
                                                        <span className={styles.autoSelectingDot} />
                                                        <span className={styles.autoSelectingDot} />
                                                        <span className={styles.autoSelectingDot} />
                                                    </span>
                                                </div>
                                            )}
                                            {isAutoSelectingPhase && showLoadingStatusMessage && activePhaseView === currentPhaseView && (
                                                <Typography
                                                    className={`${styles.message} ${styles.loadingMessage} ${
                                                        normalizedLoadingMessage.length > 110 ? styles.messageCompact : ""
                                                    }`}
                                                >
                                                    {normalizedLoadingMessage}
                                                </Typography>
                                            )}
                                            {showCompactPhaseHint && (
                                                <div className={styles.compactPhaseHintRow} aria-live="polite">
                                                    <Typography className={`${styles.message} ${styles.loadingMessage} ${styles.compactPhaseHintText}`}>
                                                        {compactPhaseHintText}
                                                    </Typography>
                                                </div>
                                            )}
                                        </div>

                                        {canViewDiagnostics && (
                                            <div
                                                className={`${styles.phaseDiagnosticsStack} ${
                                                    isOverviewCollapsed && activePhaseView !== "skip" ? styles.phaseDiagnosticsStackOverviewHidden : ""
                                                }`}
                                            >
                                                <div className={`${styles.phaseMetaCard} ${isOverviewCollapsed ? styles.phaseMetaCardCollapsed : ""}`}>
                                                    <div className={styles.phaseMetaHeader}>
                                                        <Typography className={styles.phaseMetaTitle}>{activePhaseLabel}</Typography>
                                                        <button
                                                            type="button"
                                                            className={styles.phaseMetaToggle}
                                                            onClick={() => setIsOverviewCollapsed(v => !v)}
                                                            title={isOverviewCollapsed ? overviewShowLabel : overviewHideLabel}
                                                        >
                                                            {isOverviewCollapsed ? "▸" : "▾"}
                                                        </button>
                                                    </div>
                                                    {!isOverviewCollapsed && (
                                                        <div className={styles.phaseMetaGrid}>
                                                            {phaseContextRows.map(row => (
                                                                <div key={`${activePhaseView}-loading-${row.label}`} className={styles.phaseMetaRow}>
                                                                    <LightTooltip title={row.label} placement="top" arrow>
                                                                        <span className={styles.phaseMetaKey}>{row.label}</span>
                                                                    </LightTooltip>
                                                                    <LightTooltip title={row.value} placement="top" arrow>
                                                                        <span className={styles.phaseMetaValue}>{row.value}</span>
                                                                    </LightTooltip>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                                {activePhaseView === "search" && (
                                                    <div className={`${styles.phaseDetailsCard} ${isOverviewCollapsed ? styles.phaseDetailsCardExpanded : ""}`}>
                                                        <Typography className={styles.phaseDetailsTitle}>{searchDetailsTitle}</Typography>
                                                        {searchPhaseCitations.length === 0 ? (
                                                            <Typography className={styles.phaseDetailsEmpty}>{searchDetailsEmpty}</Typography>
                                                        ) : (
                                                            <div
                                                                className={`${styles.phaseDetailsList} ${
                                                                    isOverviewCollapsed ? styles.phaseDetailsListExpanded : ""
                                                                }`}
                                                            >
                                                                {searchPhaseCitations.map(citation => (
                                                                    <div key={`${citation.id}-loading`} className={styles.phaseDetailsItem}>
                                                                        <div className={styles.phaseDetailsRow}>
                                                                            <span className={styles.phaseDetailsKey}>{detailsSourceLabel}</span>
                                                                            <LightTooltip title={citation.sourceLabel} placement="top" arrow>
                                                                                <span className={styles.phaseDetailsValue}>{citation.sourceLabel}</span>
                                                                            </LightTooltip>
                                                                        </div>
                                                                        <div className={styles.phaseDetailsMetrics}>
                                                                            <span className={styles.phaseDetailsMetric}>
                                                                                {detailsRerankerScoreLabel}: {formatDetailScore(citation.rerankerScore)}
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                                {activePhaseView === "score" && (
                                                    <div className={`${styles.phaseDetailsCard} ${isOverviewCollapsed ? styles.phaseDetailsCardExpanded : ""}`}>
                                                        <Typography className={styles.phaseDetailsTitle}>{scoreDetailsTitle}</Typography>
                                                        {scorePhaseCandidates.length === 0 ? (
                                                            <Typography className={styles.phaseDetailsEmpty}>{scoreDetailsEmpty}</Typography>
                                                        ) : (
                                                            <>
                                                                {scoreComparisonCandidates.length > 0 && (
                                                                    <div className={styles.confidenceBars}>
                                                                        {scoreComparisonCandidates.map((candidate, index) => {
                                                                            const barWidth = Math.max(0, Math.min(100, Number(candidate.confidence ?? 0)));
                                                                            const phaseLabel =
                                                                                index === 0 ? autoSelectionDecisionTopLabel : autoSelectionDecisionNextLabel;
                                                                            return (
                                                                                <div key={`${candidate.id}-bar-loading`} className={styles.confidenceBarRow}>
                                                                                    <span
                                                                                        className={styles.confidenceBarLabel}
                                                                                        title={`${phaseLabel}: ${candidate.productLabel}`}
                                                                                    >
                                                                                        {phaseLabel}
                                                                                    </span>
                                                                                    <span className={styles.confidenceBarTrack}>
                                                                                        <span
                                                                                            className={`${styles.confidenceBarFill} ${
                                                                                                index === 0
                                                                                                    ? styles.confidenceBarFillTop
                                                                                                    : styles.confidenceBarFillNext
                                                                                            }`}
                                                                                            style={{ width: `${barWidth}%` }}
                                                                                        />
                                                                                    </span>
                                                                                    <span className={styles.confidenceBarValue}>
                                                                                        {candidate.confidence === null ? "-" : `${candidate.confidence}%`}
                                                                                    </span>
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                )}
                                                                <div
                                                                    className={`${styles.phaseDetailsList} ${
                                                                        isOverviewCollapsed ? styles.phaseDetailsListExpanded : ""
                                                                    }`}
                                                                >
                                                                    {scorePhaseCandidates.map(candidate => (
                                                                        <div key={`${candidate.id}-loading`} className={styles.phaseDetailsItem}>
                                                                            <LightTooltip title={candidate.productLabel} placement="top" arrow>
                                                                                <Typography className={styles.phaseScoreName}>
                                                                                    {candidate.productLabel}
                                                                                </Typography>
                                                                            </LightTooltip>
                                                                            <div className={styles.phaseDetailsMetrics}>
                                                                                <span className={styles.phaseDetailsMetric}>
                                                                                    {confidenceBadgeLabel}:{" "}
                                                                                    {candidate.confidence === null ? "-" : `${candidate.confidence}%`}
                                                                                </span>
                                                                                <span className={styles.phaseDetailsMetric}>
                                                                                    {scoreDetailsHitsLabel}: {candidate.citationCount}
                                                                                </span>
                                                                                <span className={styles.phaseDetailsMetric}>
                                                                                    {scoreDetailsAverageLabel}: {formatDetailScore(candidate.averageScore)}
                                                                                </span>
                                                                                <span className={styles.phaseDetailsMetric}>
                                                                                    {scoreDetailsPeakLabel}: {formatDetailScore(candidate.maxScore)}
                                                                                </span>
                                                                                <span className={styles.phaseDetailsMetric}>
                                                                                    {scoreDetailsSamplesLabel}: {candidate.scoreSampleCount}
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        {isSelection && (
                                            <>
                                                {showPhaseTimeline ? (
                                                    <div className={styles.phaseTimeline} aria-live="polite">
                                                        {phaseSequence.map((phase, phaseIndex) => {
                                                            const isFinalStep = phaseIndex === 2;
                                                            const stepStateClass =
                                                                phaseIndex < currentPhaseIndex
                                                                    ? styles.phaseStepComplete
                                                                    : phaseIndex === currentPhaseIndex
                                                                      ? isFinalStep && currentPhaseView === "apply"
                                                                          ? styles.phaseStepApplied
                                                                          : isFinalStep && currentPhaseView === "skip"
                                                                            ? styles.phaseStepSkip
                                                                            : styles.phaseStepActive
                                                                      : styles.phaseStepUpcoming;
                                                            const isSelectedPhase = activePhaseView === phase.key;
                                                            return (
                                                                <button
                                                                    key={`${phase.key}-${phaseIndex}`}
                                                                    type="button"
                                                                    className={`${styles.phaseStep} ${styles.phaseStepButton} ${stepStateClass} ${
                                                                        isSelectedPhase ? styles.phaseStepSelected : ""
                                                                    }`}
                                                                    onClick={() => {
                                                                        setSelectedPhaseView(phase.key);
                                                                        setIsPhaseViewPinned(true);
                                                                    }}
                                                                    aria-pressed={isSelectedPhase}
                                                                    disabled={isLocked}
                                                                >
                                                                    <span className={styles.phaseStepBullet}>{phaseIndex + 1}</span>
                                                                    <span className={styles.phaseStepLabel}>{phase.label}</span>
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                ) : (
                                                    <div
                                                        className={`${styles.loadingPhaseRow} ${activePhaseIsApply ? styles.loadingPhaseRowApply : ""} ${
                                                            activePhaseIsSkip ? styles.loadingPhaseRowSkip : ""
                                                        }`}
                                                        aria-live="polite"
                                                    >
                                                        <span
                                                            className={`${styles.loadingPhasePulse} ${
                                                                activePhaseIsApply
                                                                    ? styles.loadingPhasePulseApply
                                                                    : activePhaseIsSkip
                                                                      ? styles.loadingPhasePulseSkip
                                                                      : ""
                                                            }`}
                                                        />
                                                        <span
                                                            className={`${styles.loadingPhaseText} ${
                                                                activePhaseIsApply
                                                                    ? styles.loadingPhaseTextApply
                                                                    : activePhaseIsSkip
                                                                      ? styles.loadingPhaseTextSkip
                                                                      : ""
                                                            }`}
                                                        >
                                                            {activePhaseCardLabel}
                                                        </span>
                                                    </div>
                                                )}

                                                {canViewDiagnostics && (
                                                    <div
                                                        className={`${styles.phaseDiagnosticsStack} ${
                                                            isOverviewCollapsed && activePhaseView !== "skip" ? styles.phaseDiagnosticsStackOverviewHidden : ""
                                                        }`}
                                                    >
                                                        <div className={`${styles.phaseMetaCard} ${isOverviewCollapsed ? styles.phaseMetaCardCollapsed : ""}`}>
                                                            <div className={styles.phaseMetaHeader}>
                                                                <Typography className={styles.phaseMetaTitle}>{activePhaseLabel}</Typography>
                                                                <button
                                                                    type="button"
                                                                    className={styles.phaseMetaToggle}
                                                                    onClick={() => setIsOverviewCollapsed(v => !v)}
                                                                    title={isOverviewCollapsed ? overviewShowLabel : overviewHideLabel}
                                                                >
                                                                    {isOverviewCollapsed ? "▸" : "▾"}
                                                                </button>
                                                            </div>
                                                            {!isOverviewCollapsed && (
                                                                <>
                                                                    <div className={styles.phaseMetaGrid}>
                                                                        {phaseContextRows.map(row => (
                                                                            <div key={`${activePhaseView}-${row.label}`} className={styles.phaseMetaRow}>
                                                                                <LightTooltip title={row.label} placement="top" arrow>
                                                                                    <span className={styles.phaseMetaKey}>{row.label}</span>
                                                                                </LightTooltip>
                                                                                <LightTooltip title={row.value} placement="top" arrow>
                                                                                    <span className={styles.phaseMetaValue}>{row.value}</span>
                                                                                </LightTooltip>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                    {isManualSelectionView && manualSelectionReasonLines.length > 0 && (
                                                                        <div className={styles.phaseMetaSkipReason}>
                                                                            <span className={styles.phaseMetaSkipReasonLabel}>
                                                                                {manualSelectionSkipReasonLabel}
                                                                            </span>
                                                                            {manualSelectionReasonLines.map(reasonLine => (
                                                                                <span key={reasonLine} className={styles.phaseMetaSkipReasonText}>
                                                                                    {reasonLine}
                                                                                </span>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                </>
                                                            )}
                                                        </div>
                                                        {activePhaseView === "search" && (
                                                            <div
                                                                className={`${styles.phaseDetailsCard} ${
                                                                    isOverviewCollapsed ? styles.phaseDetailsCardExpanded : ""
                                                                }`}
                                                            >
                                                                <Typography className={styles.phaseDetailsTitle}>{searchDetailsTitle}</Typography>
                                                                {searchPhaseCitations.length === 0 ? (
                                                                    <Typography className={styles.phaseDetailsEmpty}>{searchDetailsEmpty}</Typography>
                                                                ) : (
                                                                    <div
                                                                        className={`${styles.phaseDetailsList} ${
                                                                            isOverviewCollapsed ? styles.phaseDetailsListExpanded : ""
                                                                        }`}
                                                                    >
                                                                        {searchPhaseCitations.map(citation => (
                                                                            <div key={`${citation.id}-selection`} className={styles.phaseDetailsItem}>
                                                                                <div className={styles.phaseDetailsRow}>
                                                                                    <span className={styles.phaseDetailsKey}>{detailsSourceLabel}</span>
                                                                                    <LightTooltip title={citation.sourceLabel} placement="top" arrow>
                                                                                        <span className={styles.phaseDetailsValue}>{citation.sourceLabel}</span>
                                                                                    </LightTooltip>
                                                                                </div>
                                                                                <div className={styles.phaseDetailsMetrics}>
                                                                                    <span className={styles.phaseDetailsMetric}>
                                                                                        {detailsRerankerScoreLabel}: {formatDetailScore(citation.rerankerScore)}
                                                                                    </span>
                                                                                </div>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                        {activePhaseView === "score" && (
                                                            <div
                                                                className={`${styles.phaseDetailsCard} ${
                                                                    isOverviewCollapsed ? styles.phaseDetailsCardExpanded : ""
                                                                }`}
                                                            >
                                                                <Typography className={styles.phaseDetailsTitle}>{scoreDetailsTitle}</Typography>
                                                                {scorePhaseCandidates.length === 0 ? (
                                                                    <Typography className={styles.phaseDetailsEmpty}>{scoreDetailsEmpty}</Typography>
                                                                ) : (
                                                                    <>
                                                                        {scoreComparisonCandidates.length > 0 && (
                                                                            <div className={styles.confidenceBars}>
                                                                                {scoreComparisonCandidates.map((candidate, index) => {
                                                                                    const barWidth = Math.max(
                                                                                        0,
                                                                                        Math.min(100, Number(candidate.confidence ?? 0))
                                                                                    );
                                                                                    const phaseLabel =
                                                                                        index === 0
                                                                                            ? autoSelectionDecisionTopLabel
                                                                                            : autoSelectionDecisionNextLabel;
                                                                                    return (
                                                                                        <div
                                                                                            key={`${candidate.id}-bar-sel`}
                                                                                            className={styles.confidenceBarRow}
                                                                                        >
                                                                                            <span
                                                                                                className={styles.confidenceBarLabel}
                                                                                                title={`${phaseLabel}: ${candidate.productLabel}`}
                                                                                            >
                                                                                                {phaseLabel}
                                                                                            </span>
                                                                                            <span className={styles.confidenceBarTrack}>
                                                                                                <span
                                                                                                    className={`${styles.confidenceBarFill} ${
                                                                                                        index === 0
                                                                                                            ? styles.confidenceBarFillTop
                                                                                                            : styles.confidenceBarFillNext
                                                                                                    }`}
                                                                                                    style={{ width: `${barWidth}%` }}
                                                                                                />
                                                                                            </span>
                                                                                            <span className={styles.confidenceBarValue}>
                                                                                                {candidate.confidence === null
                                                                                                    ? "-"
                                                                                                    : `${candidate.confidence}%`}
                                                                                            </span>
                                                                                        </div>
                                                                                    );
                                                                                })}
                                                                            </div>
                                                                        )}
                                                                        <div
                                                                            className={`${styles.phaseDetailsList} ${
                                                                                isOverviewCollapsed ? styles.phaseDetailsListExpanded : ""
                                                                            }`}
                                                                        >
                                                                            {scorePhaseCandidates.map(candidate => (
                                                                                <div key={`${candidate.id}-sel`} className={styles.phaseDetailsItem}>
                                                                                    <LightTooltip title={candidate.productLabel} placement="top" arrow>
                                                                                        <Typography className={styles.phaseScoreName}>
                                                                                            {candidate.productLabel}
                                                                                        </Typography>
                                                                                    </LightTooltip>
                                                                                    <div className={styles.phaseDetailsMetrics}>
                                                                                        <span className={styles.phaseDetailsMetric}>
                                                                                            {confidenceBadgeLabel}:{" "}
                                                                                            {candidate.confidence === null ? "-" : `${candidate.confidence}%`}
                                                                                        </span>
                                                                                        <span className={styles.phaseDetailsMetric}>
                                                                                            {scoreDetailsHitsLabel}: {candidate.citationCount}
                                                                                        </span>
                                                                                        <span className={styles.phaseDetailsMetric}>
                                                                                            {scoreDetailsAverageLabel}:{" "}
                                                                                            {formatDetailScore(candidate.averageScore)}
                                                                                        </span>
                                                                                        <span className={styles.phaseDetailsMetric}>
                                                                                            {scoreDetailsPeakLabel}: {formatDetailScore(candidate.maxScore)}
                                                                                        </span>
                                                                                        <span className={styles.phaseDetailsMetric}>
                                                                                            {scoreDetailsSamplesLabel}: {candidate.scoreSampleCount}
                                                                                        </span>
                                                                                    </div>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    </>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </>
                                        )}

                                        {/* Message + action buttons */}
                                        {showPrimaryStatusMessage && (
                                            <Typography className={`${styles.message} ${isLongStatusMessage ? styles.messageCompact : ""}`}>
                                                {panelStatusMessage}
                                            </Typography>
                                        )}

                                        {showSelectionActions && (
                                            <div className={styles.buttons}>
                                                {canContinueWithAll && (
                                                    <Button
                                                        id="continueWithAllButton"
                                                        size="small"
                                                        appearance="primary"
                                                        className={styles.primaryButton}
                                                        icon={<CheckCircleOutlineIcon sx={{ fontSize: "13px", color: "white" }} />}
                                                        disabled={isLocked}
                                                        onClick={dispatchContinueWithAll}
                                                    >
                                                        {continueLabel}
                                                    </Button>
                                                )}
                                                <Button
                                                    id="cancelAllValidationButton"
                                                    size="small"
                                                    appearance="secondary"
                                                    className={styles.secondaryButton}
                                                    icon={<CancelOutlinedIcon sx={{ fontSize: "13px", color: "#64748b" }} />}
                                                    disabled={isLocked}
                                                    onClick={dispatchCancel}
                                                >
                                                    {cancelLabel}
                                                </Button>
                                            </div>
                                        )}
                                        {showSelectionActions && !canViewDiagnostics && isManualSelectionView && manualSelectionReasonLines.length > 0 && (
                                            <div className={styles.phaseMetaSkipReason}>
                                                <span className={styles.phaseMetaSkipReasonLabel}>{manualSelectionSkipReasonLabel}</span>
                                                {manualSelectionReasonLines.map(reasonLine => (
                                                    <span key={`${reasonLine}-compact`} className={styles.phaseMetaSkipReasonText}>
                                                        {reasonLine}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                        {showSelectionActions && (
                                            <div className={styles.scoreLegendRow}>
                                                <span className={styles.legendCountBadge}>{countBadgeLabel}</span>
                                                <span className={styles.legendConfidenceBadge}>{confidenceBadgeLabel}</span>
                                                <Typography className={styles.scoreLegendText}>{scoreLegendText}</Typography>
                                            </div>
                                        )}

                                        {/* Zero-citations empty state — shown when AI found no matching products */}
                                        {isConfirm && (
                                            <div className={styles.zeroStateWrap}>
                                                <div className={styles.zeroState}>
                                                    <SearchOffOutlinedIcon className={styles.zeroStateIcon} />
                                                    <Typography className={styles.zeroStateTitle}>{zeroSuggestionsTitle}</Typography>
                                                    <Typography className={styles.zeroStateBody}>{zeroSuggestionsBody}</Typography>
                                                    <button type="button" className={styles.zeroStateCta} onClick={() => setActiveTab("browse")}>
                                                        {zeroSuggestionsBrowseCta}
                                                    </button>
                                                </div>
                                            </div>
                                        )}

                                        {/* Tag list */}
                                        {showTagList && (
                                            <div className={`${styles.tagList} ${groupedProducts.length === 1 ? styles.tagListSingle : ""}`}>
                                                {groupedProducts.map((productGroup, groupIndex) => {
                                                    const isSingleBareMatch =
                                                        productGroup.versionGroups.size === 0 &&
                                                        productGroup.bareItems.length === 1 &&
                                                        (productGroup.bareItems[0].displayText ?? productGroup.bareItems[0].productText) ===
                                                            productGroup.productText;

                                                    if (isSingleBareMatch) {
                                                        const item = productGroup.bareItems[0];
                                                        const confidenceText = formatConfidence(item.confidence);
                                                        return (
                                                            <button
                                                                key={productGroup.productText}
                                                                type="button"
                                                                className={`${styles.compactProductRow} ${groupIndex === 0 && groupedProducts.length > 1 ? styles.compactProductRowTop : ""}`}
                                                                disabled={isLocked}
                                                                onClick={() => dispatchSelectProduct(item)}
                                                                title={item.displayText ?? item.productText}
                                                            >
                                                                <span className={styles.compactProductName}>{productGroup.productText}</span>
                                                                <span className={styles.tagMetrics}>
                                                                    <LightTooltip title={`${countBadgeLabel}: ${item.count}`} placement="top" arrow>
                                                                        <span className={styles.tagCount}>{item.count}</span>
                                                                    </LightTooltip>
                                                                    {confidenceText && (
                                                                        <LightTooltip
                                                                            title={`${confidenceBadgeLabel}: ${confidenceText}`}
                                                                            placement="top"
                                                                            arrow
                                                                        >
                                                                            <span className={styles.tagConfidence}>{confidenceText}</span>
                                                                        </LightTooltip>
                                                                    )}
                                                                </span>
                                                            </button>
                                                        );
                                                    }

                                                    {
                                                        const isGroupCollapsed = collapsedSuggestionGroups.has(productGroup.productText);
                                                        const hasCollapsibleContent = productGroup.bareItems.length > 0 || productGroup.versionGroups.size > 0;

                                                        return (
                                                            <div
                                                                key={productGroup.productText}
                                                                className={`${styles.productGroup} ${productGroup.versionGroups.size > 0 ? styles.productGroupWithVersions : ""} ${groupIndex === 0 && groupedProducts.length > 1 ? styles.productGroupTop : ""}`}
                                                            >
                                                                {/* Product header row — chevron + label */}
                                                                <div className={styles.productGroupHeader}>
                                                                    {hasCollapsibleContent && (
                                                                        <button
                                                                            type="button"
                                                                            className={styles.productGroupToggle}
                                                                            onClick={() => toggleSuggestionGroupCollapse(productGroup.productText)}
                                                                            disabled={isLocked}
                                                                            aria-expanded={!isGroupCollapsed}
                                                                            aria-label={isGroupCollapsed ? groupExpandLabel : groupCollapseLabel}
                                                                        >
                                                                            <span className={styles.productGroupChevron}>{isGroupCollapsed ? "▸" : "▾"}</span>
                                                                        </button>
                                                                    )}
                                                                    <button
                                                                        type="button"
                                                                        className={styles.productGroupLabel}
                                                                        title={productGroup.productText}
                                                                        disabled={isLocked}
                                                                        onClick={() => handleProductLabelClick(productGroup)}
                                                                    >
                                                                        {productGroup.productText}
                                                                    </button>
                                                                </div>

                                                                {!isGroupCollapsed && (
                                                                    <>
                                                                        {/* Bare items (no version) */}
                                                                        {productGroup.bareItems.length > 0 && (
                                                                            <div className={styles.tagGrid}>
                                                                                {productGroup.bareItems.map(productOption => {
                                                                                    const fullLabel = productOption.displayText ?? productOption.productText;
                                                                                    const chipLabel =
                                                                                        fullLabel.split(" > ").at(-1) ?? productOption.productText;
                                                                                    const confidenceText = formatConfidence(productOption.confidence);
                                                                                    return (
                                                                                        <LightTooltip
                                                                                            key={[
                                                                                                productOption.manual_key,
                                                                                                productOption.faq_key,
                                                                                                productOption.subProduct?.manual_sub_key ?? "",
                                                                                                productOption.subProduct?.faq_sub_key ?? "",
                                                                                                fullLabel
                                                                                            ].join("-")}
                                                                                            title={fullLabel}
                                                                                            placement="top"
                                                                                            arrow
                                                                                        >
                                                                                            <span className={styles.tagTooltipAnchor}>
                                                                                                <button
                                                                                                    type="button"
                                                                                                    className={styles.tagChip}
                                                                                                    disabled={isLocked}
                                                                                                    onClick={() => dispatchSelectProduct(productOption)}
                                                                                                    title={fullLabel}
                                                                                                >
                                                                                                    <span className={styles.tagContent}>
                                                                                                        <span className={styles.tagLabel}>{chipLabel}</span>
                                                                                                        <span className={styles.tagMetrics}>
                                                                                                            <span
                                                                                                                className={styles.tagCount}
                                                                                                                title={`${countBadgeLabel}: ${productOption.count}`}
                                                                                                            >
                                                                                                                {productOption.count}
                                                                                                            </span>
                                                                                                            {confidenceText && (
                                                                                                                <span
                                                                                                                    className={styles.tagConfidence}
                                                                                                                    title={`${confidenceBadgeLabel}: ${confidenceText}`}
                                                                                                                >
                                                                                                                    {confidenceText}
                                                                                                                </span>
                                                                                                            )}
                                                                                                        </span>
                                                                                                    </span>
                                                                                                </button>
                                                                                            </span>
                                                                                        </LightTooltip>
                                                                                    );
                                                                                })}
                                                                            </div>
                                                                        )}

                                                                        {/* Version groups */}
                                                                        <div className={styles.versionGroupsRow}>
                                                                            {[...productGroup.versionGroups.entries()].map(([vKey, vGroup]) => (
                                                                                <div key={vKey} className={styles.versionGroup}>
                                                                                    <button
                                                                                        type="button"
                                                                                        className={styles.versionGroupLabel}
                                                                                        title={vGroup.label}
                                                                                        disabled={isLocked}
                                                                                        onClick={() => handleVersionLabelClick(vGroup)}
                                                                                    >
                                                                                        {vGroup.label}
                                                                                    </button>
                                                                                    <div className={styles.tagGrid}>
                                                                                        {vGroup.items.map(productOption => {
                                                                                            const fullLabel =
                                                                                                productOption.displayText ?? productOption.productText;
                                                                                            const chipLabel =
                                                                                                fullLabel.split(" > ").at(-1) ?? productOption.productText;
                                                                                            const confidenceText = formatConfidence(productOption.confidence);
                                                                                            return (
                                                                                                <LightTooltip
                                                                                                    key={[
                                                                                                        productOption.manual_key,
                                                                                                        productOption.faq_key,
                                                                                                        productOption.productVersion?.manual_version_key ?? "",
                                                                                                        productOption.productVersion?.faq_version_key ?? "",
                                                                                                        productOption.subProduct?.manual_sub_key ?? "",
                                                                                                        productOption.subProduct?.faq_sub_key ?? "",
                                                                                                        fullLabel
                                                                                                    ].join("-")}
                                                                                                    title={fullLabel}
                                                                                                    placement="top"
                                                                                                    arrow
                                                                                                >
                                                                                                    <span className={styles.tagTooltipAnchor}>
                                                                                                        <button
                                                                                                            type="button"
                                                                                                            className={styles.tagChip}
                                                                                                            disabled={isLocked}
                                                                                                            onClick={() => dispatchSelectProduct(productOption)}
                                                                                                            title={fullLabel}
                                                                                                        >
                                                                                                            <span className={styles.tagContent}>
                                                                                                                <span className={styles.tagLabel}>
                                                                                                                    {chipLabel}
                                                                                                                </span>
                                                                                                                <span className={styles.tagMetrics}>
                                                                                                                    <span
                                                                                                                        className={styles.tagCount}
                                                                                                                        title={`${countBadgeLabel}: ${productOption.count}`}
                                                                                                                    >
                                                                                                                        {productOption.count}
                                                                                                                    </span>
                                                                                                                    {confidenceText && (
                                                                                                                        <span
                                                                                                                            className={styles.tagConfidence}
                                                                                                                            title={`${confidenceBadgeLabel}: ${confidenceText}`}
                                                                                                                        >
                                                                                                                            {confidenceText}
                                                                                                                        </span>
                                                                                                                    )}
                                                                                                                </span>
                                                                                                            </span>
                                                                                                        </button>
                                                                                                    </span>
                                                                                                </LightTooltip>
                                                                                            );
                                                                                        })}
                                                                                    </div>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    </>
                                                                )}
                                                            </div>
                                                        );
                                                    }
                                                })}
                                            </div>
                                        )}

                                        {/* Browse hint */}
                                        {showSelectionActions && (
                                            <div className={styles.browseHint}>
                                                <InfoOutlinedIcon sx={{ fontSize: "12px", flexShrink: 0, color: "#6a6ee6" }} />
                                                <span>
                                                    {browseHintText}{" "}
                                                    <button type="button" className={styles.browseHintLink} onClick={() => setActiveTab("browse")}>
                                                        {browseTabLabel}
                                                    </button>
                                                </span>
                                            </div>
                                        )}
                                    </>
                                )}
                            </>
                        ) : (
                            /* ── Browse Products tab ── */
                            <div className={styles.browseTab}>
                                {isZeroSuggestions && (
                                    <div className={styles.browseCallout}>
                                        <div className={styles.browseCalloutHeader}>
                                            <SearchOffOutlinedIcon className={styles.browseCalloutIcon} />
                                            <Typography className={styles.browseCalloutTitle}>{zeroSuggestionsTitle}</Typography>
                                        </div>
                                        <Typography className={styles.browseCalloutBody}>{zeroSuggestionsBrowseLead}</Typography>
                                        <div className={styles.browseCalloutActions}>
                                            {canContinueWithAll && (
                                                <Button
                                                    id="browseContinueWithAllButton"
                                                    size="small"
                                                    appearance="primary"
                                                    className={styles.primaryButton}
                                                    icon={<CheckCircleOutlineIcon sx={{ fontSize: "13px", color: "white" }} />}
                                                    disabled={isLocked}
                                                    onClick={dispatchContinueWithAll}
                                                >
                                                    {continueLabel}
                                                </Button>
                                            )}
                                            <Button
                                                id="browseCancelAllValidationButton"
                                                size="small"
                                                appearance="secondary"
                                                className={styles.secondaryButton}
                                                icon={<CancelOutlinedIcon sx={{ fontSize: "13px", color: "#64748b" }} />}
                                                disabled={isLocked}
                                                onClick={dispatchCancel}
                                            >
                                                {cancelLabel}
                                            </Button>
                                        </div>
                                    </div>
                                )}

                                <div className={styles.browseInfoRow}>
                                    <InfoOutlinedIcon sx={{ fontSize: "12px", flexShrink: 0, color: "#6a6ee6" }} />
                                    <span>{browseCatalogHelpText}</span>
                                </div>

                                {/* Search input */}
                                <div className={styles.browseSearchRow}>
                                    <SearchOutlinedIcon sx={{ fontSize: "14px", color: "#6a6ee6", flexShrink: 0 }} />
                                    <input
                                        className={styles.browseSearchInput}
                                        type="text"
                                        placeholder={browseSearchPlaceholder}
                                        value={browseSearch}
                                        disabled={browsableProducts.length === 0}
                                        onChange={e => {
                                            setBrowseSearch(e.target.value);
                                        }}
                                    />
                                    {browseSearch && (
                                        <button
                                            type="button"
                                            className={styles.browseSearchClear}
                                            onClick={() => {
                                                setBrowseSearch("");
                                            }}
                                        >
                                            ×
                                        </button>
                                    )}
                                    {browsableProducts.length > 0 && (
                                        <span className={styles.browseProductCount}>
                                            {isBrowseSearchActive
                                                ? `${filteredBrowseResults.length} / ${browsableProducts.length}`
                                                : `${browsableProducts.length}`}
                                        </span>
                                    )}
                                </div>

                                {/* Hierarchy list */}
                                <div ref={browseListRef} className={styles.browseList}>
                                    {browsableProducts.length === 0 ? (
                                        <div className={styles.browseCatalogEmpty}>
                                            <CategoryOutlinedIcon className={styles.browseCatalogEmptyIcon} />
                                            <Typography className={styles.browseCatalogEmptyText}>{browseCatalogEmptyText}</Typography>
                                        </div>
                                    ) : isBrowseSearchActive && filteredBrowseResults.length === 0 ? (
                                        <div className={styles.browseEmptyStateCentered}>
                                            <SearchOffOutlinedIcon className={styles.browseEmptyIcon} />
                                            <Typography className={styles.browseEmptyText}>{browseNoResultsText}</Typography>
                                        </div>
                                    ) : (
                                        <div className={styles.browseCatalogGrid}>{visibleBrowseCards.map(renderBrowseCatalogCard)}</div>
                                    )}
                                </div>

                                {/* Close panel button at bottom */}
                                <div className={styles.browseCancelRow}>
                                    <button type="button" className={styles.browseCloseBtn} onClick={dispatchClose} disabled={isLocked}>
                                        <CloseOutlinedIcon sx={{ fontSize: "13px" }} />
                                        {closePanelLabel}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export const AllProductSuggestionsPanel = memo(AllProductSuggestionsPanelComponent);
